import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X, Loader2, HeartCrack, Gift, Shield, Award, Star, Crown, Diamond, Gem, Flame, Check, Zap } from 'lucide-react';
import { useDailyBonusStatus, useClaimDailyBonus, CycleProgressDay } from '@/hooks/useDailyBonus';
import { useDailyBonus } from '@/contexts/DailyBonusContext';
import { useVerification } from '@/contexts/VerificationContext';
import { useVip } from '@/contexts/VipContext';
import { useToast } from '@/hooks/use-toast';
import { CountdownTimer } from './CountdownTimer';
import { GCBadgeSVG, SCBadgeSVG } from '@/components/CurrencyBadge';

interface DailyBonusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DailyBonusModal = ({ isOpen, onClose }: DailyBonusModalProps) => {
  const { toast } = useToast();
  const { status, loading, error, refetch } = useDailyBonusStatus();
  const { refetch: refetchContext, streakBrokenInfo, clearStreakBrokenInfo } = useDailyBonus();
  const { claim, claiming, claimResult, reset } = useClaimDailyBonus();
  const { checkVerificationAndPrompt } = useVerification();
  const { userVipStatus } = useVip();
  const [claimSuccess, setClaimSuccess] = useState(false);

  // Swipe to close state
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const modalContentRef = useRef<HTMLDivElement>(null);

  // Swipe to close handlers
  const handleDragHandleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    const touch = e.touches[0];
    dragStartY.current = touch.clientY;
    setIsDragging(true);
  };

  const handleDragHandleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const deltaY = touch.clientY - dragStartY.current;
    
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleDragHandleTouchEnd = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.stopPropagation();
    
    const threshold = 100;
    if (dragY > threshold) {
      onClose();
    } else {
      setDragY(0);
    }
    
    setIsDragging(false);
    dragStartY.current = 0;
  };

  useEffect(() => {
    if (!isOpen) {
      setDragY(0);
      setIsDragging(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const originalOverscrollBehavior = document.body.style.overscrollBehaviorY;
      const originalOverscrollBehaviorHtml = document.documentElement.style.overscrollBehaviorY;
      
      document.body.style.overscrollBehaviorY = 'none';
      document.documentElement.style.overscrollBehaviorY = 'none';
      
      return () => {
        document.body.style.overscrollBehaviorY = originalOverscrollBehavior;
        document.documentElement.style.overscrollBehaviorY = originalOverscrollBehaviorHtml;
      };
    }
  }, [isOpen]);

  const tierColor = userVipStatus?.tier_color || '#F59E0B';
  const tierLevel = userVipStatus?.tier_level || 1;
  
  const getTierIcon = (level: number) => {
    switch (level) {
      case 1: return Shield;
      case 2: return Award;
      case 3: return Star;
      case 4: return Crown;
      case 5: return Diamond;
      case 6: return Gem;
      case 7: return Diamond;
      case 8: return Crown;
      default: return Shield;
    }
  };
  const TierIcon = getTierIcon(tierLevel);

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      setClaimSuccess(false);
      reset();
      if (streakBrokenInfo) {
        clearStreakBrokenInfo();
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, reset, streakBrokenInfo, clearStreakBrokenInfo]);

  useEffect(() => {
    if (claimResult?.success) {
      setClaimSuccess(true);
      toast({
        title: 'Daily Bonus Claimed!',
        description: `You received ${claimResult.reward?.gc?.toLocaleString() ?? 0} GC${claimResult.reward?.sc ? ` + ${claimResult.reward.sc} SC` : ''}!`,
        variant: 'success',
      });
      refetchContext();
      refetch();
    } else if (claimResult && !claimResult.success && claimResult.message) {
      toast({
        title: 'Claim Failed',
        description: claimResult.message,
        variant: 'destructive',
      });
    }
  }, [claimResult, toast, refetchContext, refetch]);

  const handleClaim = async () => {
    if (!checkVerificationAndPrompt('data')) {
      onClose();
      return;
    }
    await claim();
  };

  const handleClose = () => {
    onClose();
  };

  const fullScreenDialogClass = "!fixed !left-0 !top-0 !right-0 !bottom-0 !w-[100vw] !h-[100vh] !max-w-none !max-h-none lg:!left-[50%] lg:!top-[50%] lg:!right-auto lg:!bottom-auto lg:!w-[95vw] lg:!max-w-lg lg:!h-auto lg:!max-h-[90vh] m-0 p-0 rounded-none lg:rounded-2xl border-0 lg:border lg:border-amber-500/20 bg-transparent overflow-hidden !translate-x-0 translate-y-0 lg:!translate-x-[-50%] lg:!translate-y-[-50%] flex flex-col [&>button]:hidden pt-[env(safe-area-inset-top)] lg:pt-0 pb-[env(safe-area-inset-bottom)] lg:pb-0 lg:shadow-[0_0_60px_rgba(251,191,36,0.15)]";

  // Loading state
  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={fullScreenDialogClass} aria-describedby={undefined}>
          <div 
            className="lg:hidden pt-3 pb-2 flex justify-center touch-none cursor-grab active:cursor-grabbing select-none bg-gradient-to-b from-card to-transparent"
            onTouchStart={handleDragHandleTouchStart}
            onTouchMove={handleDragHandleTouchMove}
            onTouchEnd={handleDragHandleTouchEnd}
          >
            <div className="w-10 h-1.5 bg-foreground/20 rounded-full" />
          </div>
          <div className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] lg:right-4 lg:top-4 z-20">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="flex h-9 w-9 lg:h-10 lg:w-10 rounded-full bg-foreground/10 hover:bg-foreground/20 border border-border"
              aria-label="Close"
            >
              <X className="h-4 w-4 lg:h-5 lg:w-5 text-muted-foreground" />
            </Button>
          </div>
          <div className="w-full flex-1 bg-card flex items-center justify-center">
            <div className="flex flex-col items-center">
              <Loader2 className="h-12 w-12 text-amber-500 animate-spin" />
              <span className="text-foreground/80 mt-4 text-base font-medium">Loading rewards...</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state
  if (error || !status?.enabled) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className={fullScreenDialogClass} aria-describedby={undefined}>
          <div 
            className="lg:hidden pt-3 pb-2 flex justify-center touch-none cursor-grab active:cursor-grabbing select-none bg-gradient-to-b from-card to-transparent"
            onTouchStart={handleDragHandleTouchStart}
            onTouchMove={handleDragHandleTouchMove}
            onTouchEnd={handleDragHandleTouchEnd}
          >
            <div className="w-10 h-1.5 bg-foreground/20 rounded-full" />
          </div>
          <div className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] lg:right-4 lg:top-4 z-20">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="flex h-9 w-9 lg:h-10 lg:w-10 rounded-full bg-foreground/10 hover:bg-foreground/20 border border-border"
              aria-label="Close"
            >
              <X className="h-4 w-4 lg:h-5 lg:w-5 text-muted-foreground" />
            </Button>
          </div>
          <div className="w-full flex-1 bg-card flex items-center justify-center">
            <div className="text-center px-6">
              <Gift className="h-16 w-16 text-amber-400/50 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">Daily Bonus Unavailable</h3>
              <p className="text-muted-foreground mb-6 text-sm">Please try again later.</p>
              <Button onClick={handleClose} className="bg-amber-500 text-white hover:bg-amber-400 font-semibold px-6">
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const canClaim = status.todayReward?.canClaim ?? false;
  const vipMultiplier = status.vipTier.multiplier;
  const hasVipBoost = vipMultiplier > 1;
  const isHotStreak = status.streak.currentStreak >= 7;
  const isMegaStreak = status.streak.currentStreak >= 14;

  const todayBaseGC = hasVipBoost ? Math.round(status.todayReward.gc / vipMultiplier) : status.todayReward.gc;
  const todayBaseSC = hasVipBoost ? Number((status.todayReward.sc / vipMultiplier).toFixed(2)) : status.todayReward.sc;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        ref={modalContentRef}
        className={fullScreenDialogClass}
        style={{
          '--tw-translate-y': dragY > 0 ? `${dragY}px` : '0px',
          opacity: dragY > 0 ? Math.max(0.6, 1 - dragY / 400) : undefined,
          transition: isDragging ? 'none' : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          overscrollBehaviorY: 'none',
        } as React.CSSProperties}
        aria-describedby={undefined}
      >
        {/* Drag Handle */}
        <div 
          className="lg:hidden pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-2 flex justify-center touch-none cursor-grab active:cursor-grabbing select-none bg-gradient-to-b from-card to-transparent absolute top-0 left-0 right-0 z-30"
          onTouchStart={handleDragHandleTouchStart}
          onTouchMove={handleDragHandleTouchMove}
          onTouchEnd={handleDragHandleTouchEnd}
        >
          <div 
            className="w-10 h-1.5 bg-foreground/20 rounded-full transition-all duration-200"
            style={{
              opacity: isDragging ? 0.6 : 1,
              transform: isDragging ? 'scaleX(0.6)' : 'scaleX(1)',
            }}
          />
        </div>

        {/* Close button */}
        <div className="absolute right-3 top-[calc(env(safe-area-inset-top)+0.75rem)] lg:right-4 lg:top-4 z-50">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="flex h-9 w-9 lg:h-10 lg:w-10 rounded-full bg-foreground/10 hover:bg-foreground/20 border border-border"
            aria-label="Close"
          >
            <X className="h-4 w-4 lg:h-5 lg:w-5 text-muted-foreground hover:text-foreground transition-colors" />
          </Button>
        </div>

        {/* Main content */}
        <div className="w-full h-full lg:h-auto overflow-hidden relative flex-1 flex flex-col bg-[#0c0a1d]">
          {/* Vibrant colorful background effects - matching sweepstakes style */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Main purple/violet gradient base */}
            <div className="absolute inset-0 bg-gradient-to-b from-purple-950/80 via-[#0c0a1d] to-[#0c0a1d]" />
            
            {/* Top center - golden/amber glow */}
            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[500px] h-[400px] bg-amber-500/30 rounded-full blur-[120px]" />
            
            {/* Left side - magenta/pink glow */}
            <div className="absolute top-20 -left-32 w-[350px] h-[350px] bg-fuchsia-600/25 rounded-full blur-[100px]" />
            
            {/* Right side - purple/violet glow */}
            <div className="absolute top-40 -right-32 w-[350px] h-[350px] bg-violet-600/25 rounded-full blur-[100px]" />
            
            {/* Bottom left - teal/cyan accent */}
            <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] bg-cyan-500/15 rounded-full blur-[80px]" />
            
            {/* Bottom right - green/emerald for claim button area */}
            <div className="absolute -bottom-20 -right-20 w-[300px] h-[300px] bg-emerald-500/15 rounded-full blur-[80px]" />
            
            {/* Center subtle purple wash */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[150px]" />
          </div>

          {/* Scrollable content */}
          <div className="relative w-full flex-1 min-h-0 flex flex-col px-4 sm:px-5 pt-14 lg:pt-5 pb-5 overflow-y-auto overflow-x-hidden overscroll-contain">
            {/* Header */}
            <div className="text-center mb-3">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">Daily Bonus</h1>
              <p className="text-sm text-muted-foreground">Collect rewards every day!</p>
            </div>

            {/* Stats Row */}
            <div className="flex items-center justify-center gap-3 mb-4">
              {/* Streak Card */}
              <div className={`
                relative flex items-center gap-3 px-4 py-3 rounded-2xl overflow-hidden
                ${isMegaStreak
                  ? 'bg-gradient-to-br from-purple-500 to-pink-600 shadow-[0_4px_20px_rgba(168,85,247,0.5)]'
                  : isHotStreak
                    ? 'bg-gradient-to-br from-orange-500 to-red-500 shadow-[0_4px_20px_rgba(251,146,60,0.5)]'
                    : 'bg-gradient-to-br from-amber-500 to-orange-500 shadow-[0_4px_20px_rgba(251,191,36,0.4)]'
                }
              `}>
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-white/10" />
                <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm">
                  <Flame className="h-6 w-6 text-white drop-shadow-lg" />
                </div>
                <div className="relative flex flex-col">
                  <span className="text-2xl font-black text-white drop-shadow-md leading-none">
                    {status.streak.currentStreak}
                  </span>
                  <span className="text-[11px] font-semibold text-white/80 uppercase tracking-wide">Day Streak</span>
                </div>
              </div>
              
              {/* VIP Multiplier Card */}
              <div 
                className="relative flex items-center gap-3 px-4 py-3 rounded-2xl overflow-hidden"
                style={{
                  background: hasVipBoost 
                    ? `linear-gradient(135deg, ${tierColor} 0%, ${tierColor}dd 100%)`
                    : 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)',
                  boxShadow: hasVipBoost 
                    ? `0 4px 20px ${tierColor}60`
                    : '0 4px 12px rgba(0,0,0,0.3)',
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-white/10" />
                <div className="relative flex items-center justify-center w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm">
                  <TierIcon className="h-6 w-6 text-white drop-shadow-lg" />
                </div>
                <div className="relative flex flex-col">
                  <span className="text-2xl font-black text-white drop-shadow-md leading-none">
                    {vipMultiplier}x
                  </span>
                  <span className="text-[11px] font-semibold text-white/80 uppercase tracking-wide">
                    {hasVipBoost ? 'VIP Bonus' : 'Bonus'}
                  </span>
                </div>
              </div>
            </div>

            {/* Streak Broken Message */}
            {streakBrokenInfo?.streakBroken && streakBrokenInfo.previousStreak && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                <HeartCrack className="h-5 w-5 text-red-400 flex-shrink-0" />
                <div>
                  <span className="text-red-400 font-semibold text-sm">Streak Reset!</span>
                  <p className="text-xs text-red-400/70">
                    Your {streakBrokenInfo.previousStreak}-day streak ended. Start fresh!
                  </p>
                </div>
              </div>
            )}

            {/* 7-Day Progress Section */}
            <div className="mb-4 bg-gradient-to-br from-purple-900/40 to-purple-950/60 backdrop-blur-sm rounded-xl p-3 border border-purple-500/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-amber-400 uppercase tracking-wide">
                  7-Day Rewards Cycle
                </span>
                <span className="text-xs font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 px-2.5 py-0.5 rounded-full shadow-sm">
                  Day {status.streak.currentCycleDay} of 7
                </span>
              </div>

              {/* Progress Bar */}
              <div className="relative h-2 bg-muted/80 rounded-full mb-3 overflow-hidden">
                <div 
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 via-green-400 to-emerald-400 rounded-full transition-all duration-700 ease-out shadow-[0_0_10px_rgba(52,211,153,0.5)]"
                  style={{ width: `${Math.max(5, ((status.streak.currentCycleDay - (canClaim ? 1 : 0)) / 7) * 100)}%` }}
                />
              </div>

              {/* Day Cards Grid */}
              <div className="grid grid-cols-3 gap-2">
                {status.cycleProgress.map((day) => (
                  <DayCard 
                    key={day.day}
                    day={day} 
                    vipMultiplier={vipMultiplier}
                    hasVipBoost={hasVipBoost}
                    isFullWidth={day.day === 7}
                    className={day.day === 7 ? 'col-span-3' : ''}
                  />
                ))}
              </div>
            </div>

            {/* Today's Reward / Countdown Section */}
            <div>
              {canClaim && !claimSuccess ? (
                <>
                  {/* Claim Button with integrated reward display */}
                  <div className="relative bg-gradient-to-br from-purple-900/50 to-emerald-900/30 border border-emerald-500/40 rounded-xl p-3 shadow-[0_0_20px_rgba(52,211,153,0.2)]">
                    {/* Reward preview row */}
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <div className="flex items-center gap-1.5">
                        <GCBadgeSVG size={22} />
                        <span className="text-lg font-bold text-foreground">{status.todayReward.gc.toLocaleString()}</span>
                      </div>
                      {status.todayReward.sc > 0 && (
                        <>
                          <span className="text-foreground/40">+</span>
                          <div className="flex items-center gap-1.5">
                            <SCBadgeSVG size={22} />
                            <span className="text-lg font-bold text-emerald-400">{status.todayReward.sc}</span>
                          </div>
                        </>
                      )}
                    </div>
                    
                    <Button
                      onClick={handleClaim}
                      disabled={claiming}
                      className="w-full h-11 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white text-base font-bold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all rounded-lg relative overflow-hidden group"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                      {claiming ? (
                        <span className="relative flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Claiming...
                        </span>
                      ) : (
                        <span className="relative font-bold">Claim Reward</span>
                      )}
                    </Button>
                  </div>
                </>
              ) : claimSuccess && claimResult?.success ? (
                /* Success State */
                <div className="relative p-5 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-emerald-400 to-green-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <Check className="w-6 h-6 text-white" strokeWidth={3} />
                  </div>
                  <h3 className="text-lg font-bold text-emerald-400 mb-1">Claimed!</h3>
                  <p className="text-base text-foreground font-semibold">
                    {(claimResult.reward?.gc ?? 0).toLocaleString()} GC
                    {(claimResult.reward?.sc ?? 0) > 0 ? ` + ${claimResult.reward?.sc} SC` : ''}
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">Come back tomorrow for more!</p>
                </div>
              ) : (
                /* Countdown State */
                <div className="bg-purple-900/30 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
                  <CountdownTimer canClaimNow={false} />
                </div>
              )}
            </div>

            {/* VIP Upsell */}
            {!status.vipTier.isMaxTier && (
              <p className="text-center text-[11px] text-muted-foreground/60 mt-3">
                Upgrade your VIP tier for up to <span className="text-amber-500 font-semibold">{status.vipTier.maxMultiplier}x</span> daily bonus
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Day Card Component
interface DayCardProps {
  day: CycleProgressDay;
  vipMultiplier: number;
  hasVipBoost: boolean;
  isFullWidth?: boolean;
  className?: string;
}

const DayCard = ({ day, vipMultiplier, hasVipBoost, isFullWidth = false, className = '' }: DayCardProps) => {
  const isCurrent = day.status === 'current';
  const isEarned = day.status === 'earned';
  const isClaimed = day.status === 'claimed';
  const isUpcoming = day.status === 'upcoming';
  const isExpired = day.status === 'expired';
  const isActiveDay = isCurrent || isEarned;
  const isDay7 = day.day === 7;

  const shouldApplyVip = hasVipBoost && (isClaimed || isActiveDay || isUpcoming);
  
  const finalGC = shouldApplyVip ? Math.floor(day.gc * vipMultiplier) : day.gc;
  const finalSC = shouldApplyVip ? Number((day.sc * vipMultiplier).toFixed(2)) : day.sc;
  
  const showGCBonus = shouldApplyVip && finalGC !== day.gc;
  const showSCBonus = shouldApplyVip && finalSC !== day.sc;
  const hasSC = finalSC > 0;

  const formatGC = (amount: number): string => {
    return Math.floor(amount).toLocaleString('en-US');
  };

  const formatSC = (amount: number): string => {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Day 7 special layout - full width, centered
  if (isFullWidth) {
    return (
      <div
        className={`
          relative flex items-center justify-center gap-4 rounded-lg p-2.5 transition-all
          ${isClaimed ? 'bg-emerald-500/20 border border-emerald-500/40' : ''}
          ${isActiveDay ? 'bg-amber-500/20 border border-amber-500/40 ring-1 ring-amber-500/30' : ''}
          ${isUpcoming ? 'bg-muted/40 border border-border' : ''}
          ${isExpired ? 'bg-muted/20 opacity-50 border border-transparent' : ''}
          ${className}
        `}
      >
        {/* Day Label */}
        <span className={`text-xs font-semibold ${
          isClaimed ? 'text-emerald-400' :
          isActiveDay ? 'text-amber-400' :
          'text-muted-foreground'
        }`}>
          Day 7+
        </span>

        {/* Rewards */}
        <div className="flex items-center gap-3">
          {/* GC */}
          <div className="flex items-center gap-1">
            <GCBadgeSVG size={18} className={isUpcoming || isExpired ? 'opacity-60' : ''} />
            {showGCBonus && (
              <span className="text-[10px] font-medium text-foreground/30 line-through">
                {formatGC(day.gc)}
              </span>
            )}
            <span className={`text-sm font-bold ${
              isClaimed ? 'text-white' :
              isActiveDay ? 'text-amber-200' :
              'text-foreground/70'
            }`}>
              {formatGC(finalGC)}
            </span>
          </div>
          {/* SC */}
          {hasSC && (
            <div className="flex items-center gap-1">
              <SCBadgeSVG size={18} className={isUpcoming || isExpired ? 'opacity-60' : ''} />
              {showSCBonus && (
                <span className="text-[10px] font-medium text-emerald-500/40 line-through">
                  {formatSC(day.sc)}
                </span>
              )}
              <span className={`text-sm font-semibold ${
                isClaimed ? 'text-emerald-300' :
                isActiveDay ? 'text-green-300' :
                'text-emerald-500/70'
              }`}>
                {formatSC(finalSC)}
              </span>
            </div>
          )}
        </div>

        {/* Current day indicator */}
        {isActiveDay && (
          <div className="absolute inset-0 rounded-lg bg-amber-400/10 animate-pulse pointer-events-none" />
        )}
      </div>
    );
  }

  // Standard layout for Days 1-6
  return (
    <div
      className={`
        relative flex flex-col items-center justify-center rounded-lg p-2 transition-all min-h-[70px]
        ${isClaimed ? 'bg-emerald-500/20 border border-emerald-500/40' : ''}
        ${isActiveDay ? 'bg-amber-500/20 border border-amber-500/40 ring-1 ring-amber-500/30 scale-[1.02]' : ''}
        ${isUpcoming ? 'bg-muted/40 border border-border' : ''}
        ${isExpired ? 'bg-muted/20 opacity-50 border border-transparent' : ''}
      `}
    >
      {/* Day Label with checkmark for claimed */}
      <div className="flex items-center gap-1 mb-1">
        {isClaimed && (
          <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center">
            <Check className="w-2 h-2 text-white" strokeWidth={3} />
          </div>
        )}
        <span className={`text-[10px] font-semibold uppercase ${
          isClaimed ? 'text-emerald-400' :
          isActiveDay ? 'text-amber-400' :
          'text-muted-foreground'
        }`}>
          Day {day.day}
        </span>
      </div>

      {/* Rewards */}
      <div className="flex flex-col items-center gap-0.5">
        {/* GC */}
        <div className="flex items-center gap-1">
          <GCBadgeSVG size={16} className={isUpcoming || isExpired ? 'opacity-60' : ''} />
          {showGCBonus && (
            <span className="text-[9px] font-medium text-foreground/30 line-through">
              {formatGC(day.gc)}
            </span>
          )}
          <span className={`text-xs font-bold ${
            isClaimed ? 'text-white' :
            isActiveDay ? 'text-amber-200' :
            'text-foreground/70'
          }`}>
            {formatGC(finalGC)}
          </span>
        </div>
        {/* SC */}
        {hasSC && (
          <div className="flex items-center gap-1">
            <SCBadgeSVG size={16} className={isUpcoming || isExpired ? 'opacity-60' : ''} />
            {showSCBonus && (
              <span className="text-[9px] font-medium text-emerald-500/40 line-through">
                {formatSC(day.sc)}
              </span>
            )}
            <span className={`text-[11px] font-semibold ${
              isClaimed ? 'text-emerald-300' :
              isActiveDay ? 'text-green-300' :
              'text-emerald-500/70'
            }`}>
              {formatSC(finalSC)}
            </span>
          </div>
        )}
      </div>

      {/* Current day pulse */}
      {isActiveDay && (
        <div className="absolute inset-0 rounded-lg bg-amber-400/10 animate-pulse pointer-events-none" />
      )}
    </div>
  );
};
